import React, { useEffect, useRef, useState } from "react";
import SVGIcon from "web/react/components/svg-icon/svg-icon";
import { Link } from "web/react/emo/link";
import { Text } from "web/react/emo/text";
import useCopyToClipboard from "web/react/hooks/use-copy-to-clip-board";
import { useIntersectionObserver } from "web/react/hooks/use-intersection-observer/use-intersection-observer";
import analytics from "web/script/analytics/analytics";
import { gettext } from "web/script/modules/django-i18n";
import { getUrl } from "web/script/routing/getUrl";
import { formatDateMDY } from "web/script/utils/dates";
import * as styles from "./promotion.css";

export interface PromotionProps {
    message: string;
    promoCode: string;
    expirationDateTimestamp?: string | null;
    isActive: boolean;
    pageType: string;
    promoAnalyticsItemData?: any;
}

export function Promotion({
    message,
    promoCode,
    isActive,
    pageType,
    expirationDateTimestamp,
    promoAnalyticsItemData,
}: PromotionProps): React.ReactElement {
    const [isCodeCopied, setIsCodeCopied] = useState<boolean>(false);
    const promotionsCouponRef = useRef<HTMLDivElement | null>(null);
    const [isVisible] = useIntersectionObserver(
        {
            rootMargin: "0px",
            threshold: 1,
            once: true,
        },
        promotionsCouponRef
    );

    useEffect(() => {
        if (isVisible) handleImpression?.();
    }, [isVisible]);

    const copy = useCopyToClipboard();

    function onCopy(): void {
        setIsCodeCopied(true);
        setTimeout(() => {
            setIsCodeCopied(false);
        }, 3000);

        copy(promoCode, pageType);

        if (!promoAnalyticsItemData?.clicked) return;
        analytics.event(
            "promotion_module",
            "clicked",
            "copy_code",
            false,
            {},
            "",
            {},
            promoAnalyticsItemData?.clicked
        );
    }

    function handleImpression(): void {
        if (!promoAnalyticsItemData?.impression) return;
        analytics.event(
            "promotion_module",
            "impression",
            "",
            false,
            {},
            "",
            {},
            promoAnalyticsItemData?.impression
        );
    }

    function getIsActiveLabel(isActive: boolean): string {
        return isActive
            ? gettext("promotion_message.designer.expires_label")
            : gettext("promotion_message.designer.expired_label");
    }

    function getExpiryText(date: string | null | undefined): string {
        if (!date) return gettext("promotion_message.promotion_text.limited_time_promotion");
        return `${getIsActiveLabel(isActive)}: ${formatDateMDY(date, true)}`; // Expires: Mon, July 5 2025
    }

    return (
        <div className={styles.container} ref={promotionsCouponRef}>
            <div className={styles.promoTextWrapper}>
                <SVGIcon name="tag-fill" className={styles.tagIcon} flip />
                <div className={styles.textContainer}>
                    <Text className={styles.text} as="p" textStyle="body-s">
                        {message}
                    </Text>
                    {promoCode && (
                        <div className={styles.promoWrapper}>
                            <Text className={styles.text} as="p" textStyle="body-s">
                                {gettext("promotion_message.promotions_text.use_code")}
                            </Text>
                            {isCodeCopied ? (
                                <div className={styles.code} onClick={onCopy}>
                                    <Text className={styles.text} as="p" textStyle="body-s-bold">
                                        {gettext(
                                            "promotion_message.promotion_text.copied_to_clipboard"
                                        )}
                                    </Text>
                                    <SVGIcon
                                        name="tick-new"
                                        className={styles.copyIcon}
                                        rotate={20}
                                    />
                                </div>
                            ) : (
                                <div className={styles.code} onClick={onCopy}>
                                    <Text
                                        className={styles.text}
                                        as="p"
                                        textStyle="body-s-bold"
                                        underline
                                    >
                                        {promoCode}
                                    </Text>
                                    <SVGIcon name="copy" className={styles.copyIcon} />
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>
            <div className={styles.expiryLegalWrapper}>
                <div className={styles.expiryWrapper}>
                    <Text as="p" textStyle="body-s" color={isActive ? "secondary" : "error"}>
                        {getExpiryText(expirationDateTimestamp)}
                    </Text>
                </div>
                <Link
                    href={getUrl("helpTermsAndConditions") || "/help/terms-and-conditions/"}
                    title={gettext("terms_conditions.terms_conditions_text.view_tcs")}
                    color="secondary"
                    bold={false}
                />
            </div>
        </div>
    );
}
